import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';

/** 1×1 PNG горизонтальной пунктирной линии (web-client UI/DescriptionList). */
const DOTTED_LINE_DATA_URI =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAMAAAABCAAAAAA+i0toAAAAAnRSTlMA/1uRIrUAAAAMSURBVHheY7j1/z8ABY8C2UtBe8oAAAAASUVORK5CYII';

const ListRoot = styled.div`
  word-wrap: break-word;

  & > dl {
    margin: 0;
    background: inherit;
  }
`;

const KeySpan = styled.span`
  position: relative;
  z-index: 1;
  padding-right: ${({ theme }) => theme.space[1]}px;
  background: inherit;
`;

const PropertyKey = styled.dt<{
  $dotted?: boolean;
  $primary?: boolean;
  $level: number;
  $widthKey: string;
}>(
  ({ theme, $dotted, $primary, $level, $widthKey }) => css`
    position: relative;
    z-index: 0;
    margin: 0;
    margin-right: ${theme.space[2]}px;
    padding-left: ${theme.space[2] * $level}px;
    flex-basis: calc(${$widthKey});
    flex-shrink: 0;
    background: inherit;
    word-break: break-word;
    color: ${theme.colors.text.secondary};
    font-size: ${theme.fontSizes[5]}px;
    line-height: 1.5;

    ${$dotted &&
    css`
      &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        height: 1px;
        margin-top: 1em;
        margin-left: ${theme.space[2] * $level}px;
        background: url(${DOTTED_LINE_DATA_URI}) 0 0 repeat-x;
      }
    `}

    ${$primary &&
    css`
      font-weight: 600;
      color: ${theme.colors.text.primary};
    `}
  `,
);

const PropertyValue = styled.dd<{ $widthKey: string }>(
  ({ theme, $widthKey }) => css`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    flex-basis: calc(100% - (${$widthKey}));
    min-width: 0;
    margin: 0;
    word-break: break-word;
    color: ${theme.colors.text.primary};
    font-size: ${theme.fontSizes[5]}px;
    line-height: 1.5;
  `,
);

const PropertyRow = styled.div<{ $primary?: boolean }>(
  ({ theme, $primary }) => css`
    display: flex;
    flex-grow: 1;
    min-width: 0;
    background: inherit;
    margin-bottom: ${theme.space[$primary ? 2 : 1]}px;

    &:last-child {
      margin-bottom: 0;
    }
  `,
);

const PlaceholderText = styled.span`
  color: ${({ theme }) => theme.colors.text.disabled};
`;

export interface DescriptionPropertyProps {
  label: string;
  /** Ширина колонки ключей, по умолчанию как в web-client — 50%. */
  widthKey?: string;
  /** Пунктирный лидер между подписью и значением. */
  dotted?: boolean;
  /** Подпись как в web-client `primary` на DescriptionList. */
  primary?: boolean;
  placeholder?: ReactNode;
  children?: ReactNode;
}

function DescriptionProperty({
  label,
  widthKey = '50%',
  dotted = false,
  primary = false,
  placeholder = 'Не указано',
  children,
}: DescriptionPropertyProps) {
  const hasValue =
    children !== undefined && children !== null && !(typeof children === 'string' && children === '');
  return (
    <PropertyRow $primary={primary}>
      <PropertyKey
        aria-label="property key"
        $dotted={dotted}
        $primary={primary}
        $level={0}
        $widthKey={widthKey}
      >
        {dotted ? <KeySpan>{label}</KeySpan> : label}
      </PropertyKey>
      <PropertyValue $widthKey={widthKey}>
        {hasValue ? children : <PlaceholderText>{placeholder}</PlaceholderText>}
      </PropertyValue>
    </PropertyRow>
  );
}

export interface DescriptionListProps {
  children: ReactNode;
  className?: string;
}

function DescriptionListRoot({ children, className }: DescriptionListProps) {
  return (
    <ListRoot className={className}>
      <dl role="presentation">{children}</dl>
    </ListRoot>
  );
}

export const DescriptionList = Object.assign(DescriptionListRoot, {
  Property: DescriptionProperty,
});
